/**
 * Wallet data generator matching new BFF API spec
 */

import { faker } from '@faker-js/faker'
import type {
  WalletWithMetrics,
  WalletDailyData,
  WalletVolumeByCurrency
} from '../../shared/types/wallets'
import type { Merchant } from '../../shared/types/merchants'
import type { CurrencyCode } from '../../shared/types/common'

faker.seed(54321)

/**
 * Internal wallet type with additional fields for mock relationships.
 * Extends WalletWithMetrics but overrides id to string for relationship linking.
 */
export interface InternalWallet extends Omit<WalletWithMetrics, 'id'> {
  id: string
  merchantId: string
  currency: CurrencyCode
  balance: number
  status: 'active' | 'inactive' | 'suspended'
}

const WALLET_NAMES = [
  'Wallet Principal',
  'Wallet Premium',
  'Wallet Business',
  'Wallet Enterprise',
  'Wallet Starter'
]

const CURRENCIES: CurrencyCode[] = ['BRL', 'USD', 'MXN', 'CLP', 'ARS']

/**
 * Generate daily data for the last 30 days
 */
function generateDailyData(_walletIndex: number): WalletDailyData[] {
  const dailyData: WalletDailyData[] = []
  const today = new Date(2026, 0, 15) // January 15, 2026

  for (let i = 29; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(date.getDate() - i)

    // Random transaction volume with some zero days
    const hasActivity = Math.random() > 0.15 // 85% chance of activity
    const volume_usd = hasActivity
      ? faker.number.float({ min: 100, max: 5000, fractionDigits: 2 })
      : 0
    const transaction_count = hasActivity
      ? faker.number.int({ min: 1, max: 20 })
      : 0

    dailyData.push({
      date: date.toISOString().split('T')[0]!,
      volume_usd,
      transaction_count
    })
  }

  return dailyData
}

/**
 * Generate volume by currency breakdown
 */
function generateVolumeByCurrency(): WalletVolumeByCurrency[] {
  const currencyCount = faker.number.int({ min: 1, max: 4 })
  const selectedCurrencies = faker.helpers.arrayElements(CURRENCIES, currencyCount)

  return selectedCurrencies.map(currency => ({
    currency,
    transaction_count: faker.number.int({ min: 10, max: 200 }),
    volume: faker.number.float({ min: 5000, max: 500000, fractionDigits: 2 })
  })).sort((a, b) => b.volume - a.volume) // Sort by volume DESC
}

function generateInternalWallet(index: number, merchantId: string): InternalWallet {
  const dailyData = generateDailyData(index)
  const volumeByCurrency = generateVolumeByCurrency()

  // Calculate totals from daily data
  const total_volume_usd = dailyData.reduce((sum, day) => sum + day.volume_usd, 0)
  const transaction_count = dailyData.reduce((sum, day) => sum + day.transaction_count, 0)
  const currency = faker.helpers.arrayElement(CURRENCIES)

  return {
    id: faker.string.uuid(),
    wallet_uuid: faker.string.uuid(),
    name: WALLET_NAMES[index % WALLET_NAMES.length]!,
    email: faker.internet.email().toLowerCase(),
    total_volume_usd: Number(total_volume_usd.toFixed(2)),
    transaction_count,
    daily_data: dailyData,
    volume_by_currency: volumeByCurrency,
    // Internal fields for mock relationships
    merchantId,
    currency,
    balance: faker.number.float({ min: 1000, max: 100000, fractionDigits: 2 }),
    status: faker.helpers.arrayElement(['active', 'active', 'active', 'inactive', 'suspended'] as const)
  }
}

// Generate mock wallets linked to merchants
export function generateWallets(merchants: Merchant[]): InternalWallet[] {
  const wallets: InternalWallet[] = []
  let walletIndex = 0

  for (const merchant of merchants) {
    // Each merchant gets 1-3 wallets
    const walletCount = faker.number.int({ min: 1, max: 3 })
    for (let i = 0; i < walletCount; i++) {
      wallets.push(generateInternalWallet(walletIndex++, merchant.id))
    }
  }

  return wallets
}

/**
 * Convert InternalWallet to WalletWithMetrics list item for API responses
 */
export function toWalletListItem(wallet: InternalWallet): WalletWithMetrics {
  const { merchantId, currency, balance, status, id, ...rest } = wallet
  return {
    ...rest,
    id: parseInt(id, 16) % 1000000 || 1 // Convert UUID to numeric ID for API
  }
}
